import { useState, useCallback, useEffect } from 'react'
import { FileSpreadsheet, CheckCircle, AlertTriangle, ArrowRight, Upload, Sparkles, Save, FolderOpen, Trash2 } from 'lucide-react'
import { SpreadsheetUploadZone } from './SpreadsheetUploadZone'
import { timetableSpreadsheetApi } from '@/services/api/timetableSpreadsheetImport'
import type { SpreadsheetParseResponse, SpreadsheetImportResponse, SpreadsheetTemplateInfo } from '@/services/api/timetableSpreadsheetImport'

interface SpreadsheetImportTabProps {
  onImportComplete?: (structureId: string, entriesCount: number) => void
  onGoToFiles?: () => void
  allowTemplateWrite?: boolean
}

type Step = 'upload' | 'preview' | 'importing' | 'done'

const TIMETABLE_FIELDS = [
  { key: 'teacher_code', label: 'Lehrer-Code', required: true },
  { key: 'day_of_week', label: 'Wochentag', required: false },
  { key: 'time_slot_label', label: 'Zeit/Stunde', required: false },
  { key: 'subject', label: 'Fach', required: false },
  { key: 'room', label: 'Raum', required: false },
  { key: 'class_name', label: 'Klasse', required: false },
  { key: 'faculty', label: 'Fachbereich', required: false },
]

export function SpreadsheetImportTab({ onImportComplete, onGoToFiles, allowTemplateWrite = true }: SpreadsheetImportTabProps) {
  const [step, setStep] = useState<Step>('upload')
  const [parseResult, setParseResult] = useState<SpreadsheetParseResponse | null>(null)
  const [importResult, setImportResult] = useState<SpreadsheetImportResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({})
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [templates, setTemplates] = useState<SpreadsheetTemplateInfo[]>([])
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
  const [showSaveTemplate, setShowSaveTemplate] = useState(false)
  const [templateName, setTemplateName] = useState('')

  useEffect(() => {
    timetableSpreadsheetApi.listTemplates()
      .then(t => setTemplates(t))
      .catch(() => {})
  }, [])

  const handleUpload = useCallback(async (file: File) => {
    setError(null)
    setSelectedFile(file)
    try {
      const result = await timetableSpreadsheetApi.parseSpreadsheet(file)
      if (!result.success) {
        setError(result.error || 'Parsing fehlgeschlagen')
        return
      }
      setParseResult(result)
      setColumnMapping(result.columns_detected || {})
      setStep('preview')
    } catch (err: any) {
      setError(err.detail || err.message || 'Upload fehlgeschlagen')
    }
  }, [])

  const handleImport = useCallback(async () => {
    if (!parseResult || !selectedFile) return
    setError(null)
    setStep('importing')

    try {
      // Upload and parse the file (this now returns uploaded_file_id)
      const result = await timetableSpreadsheetApi.parseSpreadsheet(selectedFile)
      if (!result.success) {
        setError(result.error || 'Parsing fehlgeschlagen')
        setStep('preview')
        return
      }

      // Now import using the uploaded file
      const importResult = await timetableSpreadsheetApi.importFromUploadedFile(
        result.uploaded_file_id!,
        columnMapping,
        selectedTemplateId || undefined
      )

      if (!importResult.success) {
        setError(importResult.error || 'Import fehlgeschlagen')
        setStep('preview')
        return
      }

      setImportResult(importResult)
      setStep('done')
      onImportComplete?.(importResult.structure_id || 'temp', importResult.entries_count)
    } catch (err: any) {
      setError(err.detail || err.message || 'Import fehlgeschlagen')
      setStep('preview')
    }
  }, [parseResult, selectedFile, columnMapping, selectedTemplateId, onImportComplete])

  const handleReset = useCallback(() => {
    setStep('upload')
    setParseResult(null)
    setImportResult(null)
    setError(null)
    setColumnMapping({})
    setSelectedFile(null)
    setSelectedTemplateId(null)
    setShowSaveTemplate(false)
    setTemplateName('')
  }, [])

  const handleSaveTemplate = useCallback(async () => {
    if (!templateName.trim() || !parseResult) return
    try {
      await timetableSpreadsheetApi.saveTemplate({
        name: templateName.trim(),
        column_mapping: columnMapping,
        format_hint: parseResult.format_detected,
      })
      setShowSaveTemplate(false)
      setTemplateName('')
      const updated = await timetableSpreadsheetApi.listTemplates()
      setTemplates(updated)
    } catch (err: any) {
      setError(err.detail || 'Template speichern fehlgeschlagen')
    }
  }, [templateName, columnMapping, parseResult])

  const handleDeleteTemplate = useCallback(async (templateId: string) => {
    try {
      await timetableSpreadsheetApi.deleteTemplate(templateId)
      setTemplates(prev => prev.filter(t => t.id !== templateId))
      if (selectedTemplateId === templateId) {
        setSelectedTemplateId(null)
      }
    } catch (err: any) {
      setError(err.detail || 'Template löschen fehlgeschlagen')
    }
  }, [selectedTemplateId])

  const handleLoadTemplate = useCallback((template: SpreadsheetTemplateInfo) => {
    setSelectedTemplateId(template.id)
    setColumnMapping(template.column_mapping)
  }, [])

  const formatLabels: Record<string, string> = {
    webuntis: 'WebUntis',
    stida: 'STiDA',
    unknown: 'Unbekanntes Format',
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-4">
        <FileSpreadsheet className="w-5 h-5 text-blue-600" />
        <h2 className="text-lg font-semibold">CSV/Excel Stundenplan-Import</h2>
      </div>

      {error && (
        <div className="p-3 bg-red-100 text-red-700 rounded flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">&times;</button>
        </div>
      )}

      {/* Step indicator */}
      <div className="flex items-center gap-2 text-sm">
        {(['upload', 'preview', 'importing', 'done'] as Step[]).map((s, i) => {
          const steps: Step[] = ['upload', 'preview', 'importing', 'done']
          const currentIdx = steps.indexOf(step)
          const isActive = i <= currentIdx
          return (
            <div key={s} className={`flex items-center gap-2 ${isActive ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                isActive ? 'bg-blue-600 text-white' : 'bg-gray-200'
              }`}>
                {i + 1}
              </div>
              <span className="capitalize">{s === 'upload' ? 'Upload' : s === 'preview' ? 'Vorschau' : s === 'importing' ? 'Import' : 'Fertig'}</span>
              {i < 3 && <ArrowRight className="w-4 h-4" />}
            </div>
          )
        })}
      </div>

      {/* Upload step */}
      {step === 'upload' && (
        <div>
          <SpreadsheetUploadZone onUpload={handleUpload} />
          <div className="mt-4 text-sm text-gray-500">
            <p>Unterstuetzte Formate: CSV, Excel (.xlsx, .xlsm)</p>
            <p className="mt-1">Bekannte Formate (WebUntis, STiDA) werden automatisch erkannt.</p>
            <p className="mt-1">Unbekannte Formate werden per KI-Analyse verarbeitet.</p>
          </div>
        </div>
      )}

      {/* Preview step */}
      {step === 'preview' && parseResult && (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded p-3 flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span className="text-green-700">
              {parseResult.entries_count} Eintraege erkannt
              {parseResult.format_detected && ` (Format: ${formatLabels[parseResult.format_detected] || parseResult.format_detected})`}
            </span>
            {parseResult.ki_analysis_used && (
              <span className="ml-2 flex items-center gap-1 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                <Sparkles className="w-3 h-3" />
                KI-Analyse
              </span>
            )}
          </div>

          {/* Template section */}
          <div className="bg-white border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium flex items-center gap-2">
                <FolderOpen className="w-4 h-4" />
                Gespeicherte Templates
              </h3>
              {allowTemplateWrite && (
              <button
                onClick={() => setShowSaveTemplate(true)}
                className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                <Save className="w-3 h-3" />
                Template speichern
              </button>
              )}
            </div>

            {showSaveTemplate && (
              <div className="mb-3 p-3 bg-gray-50 rounded flex gap-2">
                <input
                  type="text"
                  value={templateName}
                  onChange={e => setTemplateName(e.target.value)}
                  placeholder="Template-Name..."
                  className="flex-1 border rounded px-2 py-1 text-sm"
                />
                <button
                  onClick={handleSaveTemplate}
                  className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                >
                  Speichern
                </button>
                <button
                  onClick={() => { setShowSaveTemplate(false); setTemplateName('') }}
                  className="px-3 py-1 border rounded text-sm hover:bg-gray-100"
                >
                  Abbrechen
                </button>
              </div>
            )}

            {templates.length > 0 ? (
              <div className="space-y-1">
                {templates.map(t => (
                  <div key={t.id} className="flex items-center justify-between p-2 rounded hover:bg-gray-50">
                    <button
                      onClick={() => handleLoadTemplate(t)}
                      className={`flex-1 text-left text-sm ${selectedTemplateId === t.id ? 'text-blue-600 font-medium' : 'text-gray-700'}`}
                    >
                      {t.name}
                      {t.format_hint && <span className="ml-2 text-xs text-gray-400">({t.format_hint})</span>}
                    </button>
                    {allowTemplateWrite && (
                    <button
                      onClick={() => handleDeleteTemplate(t.id)}
                      className="text-red-400 hover:text-red-600 p-1"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">Keine Templates gespeichert.</p>
            )}
          </div>

          {/* Column mapping */}
          <div className="bg-white border rounded-lg p-4">
            <h3 className="font-medium mb-3">Spalten-Zuordnung</h3>
            <div className="space-y-2">
              {TIMETABLE_FIELDS.map(field => (
                <div key={field.key} className="flex items-center gap-3">
                  <span className="w-32 text-sm font-medium">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </span>
                  <select
                    value={columnMapping[field.key] || ''}
                    onChange={e => setColumnMapping(prev => ({ ...prev, [field.key]: e.target.value }))}
                    className="flex-1 border rounded px-2 py-1 text-sm"
                  >
                    <option value="">-- Nicht zugeordnet --</option>
                    {Object.entries(parseResult.columns_detected || {}).map(([standard, detected]) => (
                      <option key={standard} value={detected}>{detected} ({standard})</option>
                    ))}
                  </select>
                  {columnMapping[field.key] && (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Warnings */}
          {parseResult.warnings.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
              <h4 className="font-medium text-yellow-800 mb-1">Warnungen</h4>
              <ul className="list-disc list-inside text-sm text-yellow-700">
                {parseResult.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={handleImport}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2"
            >
              <Upload className="w-4 h-4" />
              Import starten
            </button>
            <button
              onClick={handleReset}
              className="px-4 py-2 border rounded hover:bg-gray-50"
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {/* Importing step */}
      {step === 'importing' && (
        <div className="text-center py-8">
          <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-3" />
          <p className="text-gray-600">Import laeuft...</p>
        </div>
      )}

      {/* Done step */}
      {step === 'done' && importResult && (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded p-4 text-center">
            <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <h3 className="text-lg font-medium text-green-700">Import abgeschlossen</h3>
            <p className="text-green-600 mt-1">
              {importResult.entries_count} Eintraege erfolgreich importiert.
            </p>
          </div>

          {importResult.warnings.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
              <h4 className="font-medium text-yellow-800 mb-1">Warnungen</h4>
              <ul className="list-disc list-inside text-sm text-yellow-700">
                {importResult.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={handleReset}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Weitere Datei importieren
            </button>
            {onGoToFiles && (
              <button
                onClick={onGoToFiles}
                className="px-4 py-2 border rounded hover:bg-gray-50"
              >
                Zurueck zur Dateien-Uebersicht
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
